// ── Route Paths ────────────────────────────────────────────────────────────
export const ROUTE_PATHS = {
  HOME:        '/',
  STORES:      '/stores',
  LOADING:     '/scanning',
  MEAL_PLAN:   '/meal-plan',
  RECIPE:      '/recipe/:id',
  GROCERY:     '/grocery-list',
  SAVED_LISTS: '/saved-lists',
  REVIEWS:     '/reviews',
  CALENDAR:    '/calendar',
  ADMIN:       '/admin',
  MY_PANTRY:      '/my-pantry',
  FLYER:           '/flyer-deals',
  SAVED_RECIPES:   '/saved-recipes',
} as const;

// ── Saved Shopping Trip ─────────────────────────────────────────────────────
export interface SavedTrip {
  id:        string;
  name:      string;
  store:     string;
  savedAt:   string;           // ISO date string
  totalCost: number;
  meals:     string[];         // meal names
  sections:  { section: string; items: { name: string; qty: string; price: number }[] }[];
}

// ── Types ──────────────────────────────────────────────────────────────────
export interface Store {
  name:       string;
  dealCount?: number;
  color?:     string;
  text?:      string;
  emoji?:     string;
}

export interface SaleItem {
  name:         string;
  price:        number;
  was_price:    number | null;
  category:     string;
  pkg_size:     string;
  image_url:    string;
  is_on_sale:   boolean;
  discount_pct: number;
}

export interface Ingredient {
  name:     string;
  qty:      string;
  price:    number;
  on_sale:  boolean;
}

export interface SideDish {
  name: string;
  how:  string;   // cooking instructions for this side
}

export interface Meal {
  id:           string;
  meal_type:    string;
  day:          string;
  name:         string;
  tagline:      string;
  serves:       number;
  cook_time:    string;
  method:       string;
  ingredients:  Ingredient[];
  total_cost:   number;
  per_serving:  number;
  instructions: string[];
  tip:          string;
  sides?:       (string | SideDish)[];  // backward-compatible: string or {name,how}
  photo_url?:   string;
  category?:    'chef' | 'basics' | 'lazy_night';
  basic_meal?:  boolean;   // legacy
  easy_night?:  boolean;   // legacy
  nutrition?:   {
    calories:  number;
    protein_g: number;
    carbs_g:   number;
    fat_g:     number;
  };
}

export interface GrocerySection {
  section: string;
  items: {
    name:    string;
    qty:     string;
    price:   number;
    used_in: string;
  }[];
}

export interface MealPlan {
  store_name:                   string;
  week_summary:                 string;
  meals:                        Meal[];
  grocery_list:                 GrocerySection[];
  total_grocery_cost:           number;
  total_grocery_cost_no_spices: number;
  savings_vs_regular:           number;
  sale_items_found:             number;
  demo_mode?:                   boolean;
}

// ── API config ─────────────────────────────────────────────────────────────
export const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:5050';

// ── Helpers ────────────────────────────────────────────────────────────────
export function budgetLabel(perServing: number): 'Budget' | 'Mid' | 'Premium' {
  if (perServing <= 4)  return 'Budget';
  if (perServing <= 7)  return 'Mid';
  return 'Premium';
}

export function budgetColor(label: 'Budget' | 'Mid' | 'Premium') {
  if (label === 'Budget')  return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (label === 'Mid')     return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  return 'bg-destructive/10 text-destructive';
}

export function mealTypeIcon(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('breakfast')) return '🍳';
  if (t.includes('lunch'))     return '🥗';
  if (t.includes('dinner'))    return '🍽️';
  if (t.includes('snack') || t.includes('dessert')) return '🍓';
  return '🍴';
}

// bannerColor — returns Tailwind bg+text classes matching each chain's verified brand color (May 2026)
export function bannerColor(banner: string): string {
  const b = banner.toLowerCase();
  // ── Empire Company ─────────────────────────────────────────────────────
  if (b.includes('sobeys'))                                    return 'bg-[#469E41] text-white';       // green
  if (b.includes('safeway'))                                   return 'bg-[#E41720] text-white';       // red
  if (b.includes('freshco') || b.includes('fresh co'))        return 'bg-[#BDD01A] text-gray-900';    // lime
  if (b.includes('iga'))                                       return 'bg-[#F30017] text-white';       // red
  if (b.includes('thrifty'))                                   return 'bg-[#005E44] text-white';       // dark green
  // ── Loblaw Companies ───────────────────────────────────────────────────
  if (b.includes('no frills') || b.includes('nofrills'))      return 'bg-[#FADC00] text-black';       // yellow
  if (b.includes('superstore') || b.includes('real canadian')) return 'bg-[#003DA5] text-white';      // blue
  if (b.includes('loblaws'))                                   return 'bg-black text-white';           // black
  if (b.includes('zehrs'))                                     return 'bg-[#DA291C] text-white';       // red
  if (b.includes('t&t') || b.includes('t & t'))               return 'bg-[#00843D] text-white';       // green
  if (b.includes('atlantic superstore'))                       return 'bg-[#DA291C] text-white';       // red
  if (b.includes('maxi'))                                      return 'bg-[#0076A8] text-white';       // blue
  if (b.includes('provigo'))                                   return 'bg-[#DA291C] text-white';       // red
  // ── Metro Inc. ─────────────────────────────────────────────────────────
  if (b.includes('metro'))                                     return 'bg-[#D81E05] text-white';       // red
  if (b.includes('food basic'))                                return 'bg-[#FFB500] text-gray-900';    // yellow
  // ── Independents ───────────────────────────────────────────────────────
  if (b.includes('walmart'))                                   return 'bg-[#0071CE] text-white';       // blue
  if (b.includes('giant tiger'))                               return 'bg-[#C8102E] text-white';       // red
  if (b.includes('save-on') || b.includes('save on'))         return 'bg-[#00551B] text-white';       // dark green
  if (b.includes('co-op') || b.includes('coop'))              return 'bg-[#CC0000] text-white';       // red
  return 'bg-primary text-primary-foreground';
}

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
export function sortMeals(meals: Meal[]): Meal[] {
  return [...meals].sort((a, b) => {
    const di = DAYS.indexOf(a.day) - DAYS.indexOf(b.day);
    if (di !== 0) return di;
    const types = ['breakfast','lunch','dinner','snack','dessert'];
    return types.indexOf(a.meal_type.toLowerCase()) - types.indexOf(b.meal_type.toLowerCase());
  });
}
